import Dialog from "../../components/General/ConfirmableDialog";
import { useEffect, useMemo, useState } from "react";
import useGraphToken from "../../hooks/useGraphToken";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { handleDownloadFile } from "../../api/handleDownloadFile";
import { handleViewFile } from "../../api/handleViewFile";
import { EmployeePersonalFileDto } from "../../interfaces/HR/employeePersonalFile";
import { EmployeePositionHistoryDto } from "../../interfaces/HR/employee";
import { employeeGenderLabel } from "../../data/HR/EmployeeGender";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  Skeleton,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FolderIcon from "@mui/icons-material/Folder";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { contractTypeLabels } from "../../data/HR/ContractType";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { getEmployeePositionHistory } from "../../api/employeeService";
import { formatDate } from "../../utils/formatDate";
import { formatCurrency } from "../../utils/formatCurrency";
import dayjs from "../../utils/dayjsTimezone";
import UpdateEmployeePopup from "../../components/HR/UpdateEmployeePopup/UpdateEmployeePopup";

interface EmployeeMonthlySalaryDto {
  id: string;
  employeeId: string;
  year: number;
  month: number;
  salaryAmount: number;
  hasSalaryIncrease: boolean;
  salaryIncreasePercentage?: number | null;
  newSalaryAmount?: number | null;
  totalLoads?: number | null;
  totalMcLeodGross?: number | null;
  dispatchRate?: number | null;
  dispatchAmount?: number | null;
  bonusAmount1?: number | null;
  bonusAmount2?: number | null;
  exchangeRate?: number | null;
  mdlTotal?: number | null;
}

interface EmployeeFinancialFileDto {
  id: string;
  financialRecordId: string;
  financialRecordType: string;
  fileName: string;
  contentType: string;
  size: number;
  uploadedDate: string;
}

const formatMdlCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "MDL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const calculateDuration = (start: Date | string, end: Date | string) => {
  const s = new Date(start);
  const e = new Date(end);
  const diff = e.getTime() - s.getTime();
  const days = diff / (1000 * 60 * 60 * 24);
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  const d = Math.floor((days % 365) % 30);
  return `${years}y ${months}m ${d}d`;
};

const getTimeInCurrentPosition = (
  positionHistory?: EmployeePositionHistoryDto[],
) => {
  const currentPositionHistory = positionHistory?.find(
    (history) => history.endDate === null,
  );

  return currentPositionHistory?.duration || "N/A";
};

const calculateAge = (birthDate: string | Date) => {
  const birth = new Date(birthDate);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
};

const calculateYears = (start: string | Date, end: string | Date) => {
  const s = new Date(start);
  const e = new Date(end);
  let y = e.getFullYear() - s.getFullYear();
  const m = e.getMonth() - s.getMonth();
  if (m < 0 || (m === 0 && e.getDate() < s.getDate())) y--;
  return y;
};

const fetchMicrosoftUser = async (accessToken: string) => {
  try {
    const res = await fetch(
      "https://graph.microsoft.com/v1.0/me?$select=id,displayName,givenName,employeeId",
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      },
    );
    return await res.json();
  } catch (err) {
    return null;
  }
};

const fetchProfilePhoto = async (accessToken: string) => {
  try {
    const res = await fetch(
      "https://graph.microsoft.com/v1.0/me/photo/$value",
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!res.ok) throw new Error("Photo not found");
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    return null;
  }
};

const fetchEmployeeDocuments = async (
  token: string | null | undefined,
  employeeId: string,
) => {
  const { data } = await axios.get(
    `${apiVariable}/getEmployeeDocuments/${employeeId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return data as EmployeePersonalFileDto[];
};

const profileSections = [
  { id: "overview", label: "Overview" },
  { id: "basic-info", label: "Basic Info" },
  { id: "personal", label: "Personal" },
  { id: "contact-details", label: "Contact Details" },
  { id: "work", label: "Work" },
  { id: "payslip", label: "Payslip" },
  { id: "position-history", label: "Position History" },
  { id: "documents", label: "Documents" },
];

const HRHomePage = () => {
  const { graphToken } = useGraphToken();
  const { token, role } = useAuth();
  const navigate = useNavigate();
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [employee, setEmployee] = useState<any>();
  const [message, setMessage] = useState("");
  const [viewLoadingId, setViewLoadingId] = useState<string | number | null>(
    null,
  );
  const [downloadLoadingId, setDownloadLoadingId] = useState<
    string | number | null
  >(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState<string | null>(
    null,
  );
  const location = useLocation();
  const { id: routeEmployeeId } = useParams<{ id?: string }>();
  const state = (location.state as { employeeId?: string } | null) ?? null;
  const selectedEmployeeId = routeEmployeeId ?? state?.employeeId ?? null;
  const canViewPayslips = !selectedEmployeeId;
  const sections = useMemo(
    () =>
      profileSections.filter(
        (section) => section.id !== "payslip" || canViewPayslips,
      ),
    [canViewPayslips],
  );
  const [isProfilePhotoLoading, setIsProfilePhotoLoading] = useState(true);
  const [salaryFilesDialogOpen, setSalaryFilesDialogOpen] = useState(false);
  const [allPayslipsDialogOpen, setAllPayslipsDialogOpen] = useState(false);
  const [selectedSalary, setSelectedSalary] =
    useState<EmployeeMonthlySalaryDto | null>(null);
  const [salaryFiles, setSalaryFiles] = useState<EmployeeFinancialFileDto[]>(
    [],
  );
  const [salaryFilesLoading, setSalaryFilesLoading] = useState(false);
  const [salaryFilesError, setSalaryFilesError] = useState("");
  const [viewingSalaryFileId, setViewingSalaryFileId] = useState("");
  const [downloadingSalaryFileId, setDownloadingSalaryFileId] = useState("");

  const { data: documents, isLoading: isLoadingDocuments } = useQuery({
    queryKey: ["employeeDocuments", employee?.id],
    queryFn: () => fetchEmployeeDocuments(token, employee?.id),
    enabled: !!employee?.id && !!token,
  });

  const {
    data: positionHistory,
    isLoading: isLoadingPositionHistory,
    isError: isPositionHistoryError,
  } = useQuery({
    queryKey: ["employeePositionHistory", employee?.id],
    queryFn: () => getEmployeePositionHistory(token!, employee.id),
    enabled: !!employee?.id && !!token,
  });

  const {
    data: salaries = [],
    isLoading: isLoadingSalaries,
    isError: isSalariesError,
  } = useQuery({
    queryKey: ["personalSalaries", employee?.id],
    queryFn: async () => {
      const { data } = await axios.get<EmployeeMonthlySalaryDto[]>(
        `${apiVariable}/employee/personal/salaries`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employeeId: employee.id },
        },
      );
      return data;
    },
    enabled: canViewPayslips && !!employee?.id && !!token,
  });

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const documentCategories = useMemo(() => {
    const allDocs = documents ?? [];
    const ssmDocs = allDocs.filter((d) =>
      d.category?.toUpperCase().startsWith("SSM"),
    );
    const generalDocs = allDocs.filter((d) =>
      d.category?.toUpperCase().startsWith("GENERAL"),
    );
    const personalDocs = allDocs.filter((d) => {
      const cat = d.category?.toUpperCase() || "";
      return !cat.startsWith("SSM") && !cat.startsWith("GENERAL");
    });

    return [
      { key: "personal", label: "Personal", docs: personalDocs },
      { key: "general", label: "General", docs: generalDocs },
      {
        key: "ssm",
        label: "Security and Safety at Work (SSM)",
        docs: ssmDocs,
      },
    ];
  }, [documents]);

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      if (!token) {
        if (!isCancelled) {
          setIsProfilePhotoLoading(false);
        }
        return;
      }
      setMessage("");
      setProfilePhotoUrl(null);
      setEmployee(undefined);
      setIsProfilePhotoLoading(true);

      if (selectedEmployeeId) {
        try {
          const res = await fetch(
            `${apiVariable}/getEmployeeById/${selectedEmployeeId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (res.ok) {
            const emp = await res.json();
            if (!isCancelled) {
              setEmployee(emp);
              setProfilePhotoUrl(null);
            }
          } else if (res.status === 404) {
            if (!isCancelled) {
              setEmployee(undefined);
              setMessage("No profile was found for this employee.");
            }
          } else {
            if (!isCancelled) {
              setEmployee(undefined);
              setMessage("Failed to load employee profile.");
            }
          }
        } catch (err) {
          if (!isCancelled) {
            setEmployee(undefined);
            setMessage("Failed to load employee profile.");
          }
        } finally {
          if (!isCancelled) {
            setIsProfilePhotoLoading(false);
          }
        }
        return;
      }

      if (!graphToken) {
        if (!isCancelled) {
          setIsProfilePhotoLoading(false);
        }
        return;
      }

      const microsoftUser = await fetchMicrosoftUser(graphToken);
      const photo = await fetchProfilePhoto(graphToken);
      if (!isCancelled && photo) setProfilePhotoUrl(photo);
      if (!microsoftUser) {
        if (!isCancelled) {
          setIsProfilePhotoLoading(false);
        }
        return;
      }
      try {
        const res = await fetch(
          `${apiVariable}/getEmployeeByEmployeeId/${microsoftUser.employeeId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (res.ok) {
          const emp = await res.json();
          if (!isCancelled) {
            setEmployee(emp);
            localStorage.setItem("employeeId", emp.id);
            if (!emp.microsoftAccountId) {
              await fetch(`${apiVariable}/updateEmployee`, {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  ...emp,
                  microsoftAccountId: microsoftUser.id,
                }),
              });
            }
          }
        } else if (res.status === 404) {
          if (!isCancelled) {
            setMessage(
              "No profile was found, please contact the HR department.",
            );
          }
        } else {
          if (!isCancelled) {
            setMessage("Failed to load employee profile.");
          }
        }
      } catch (err) {
        if (!isCancelled) {
          setMessage("Failed to load employee profile.");
        }
      } finally {
        if (!isCancelled) {
          setIsProfilePhotoLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [graphToken, token, selectedEmployeeId]);

  const getEmployeeInitials = (emp?: {
    firstName?: string;
    lastName?: string;
  }) => {
    if (!emp) return "";
    const firstInitial = emp.firstName?.[0] ?? "";
    const lastInitial = emp.lastName?.[0] ?? "";
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  const NavList = (
    <List>
      {sections.map((s) => (
        <ListItem
          key={s.id}
          button
          component="a"
          href={`#${s.id}`}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            px: 2,
            color: "text.secondary",
            transition: "background-color 0.2s ease, color 0.2s ease",
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: "primary.main",
            },
          }}
        >
          {!isMdUp && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              <KeyboardArrowRightIcon />
            </ListItemIcon>
          )}
          <ListItemText primary={s.label} />
        </ListItem>
      ))}
    </List>
  );

  if (message) return <Typography>{message}</Typography>;

  const hasAnyDocuments = documentCategories.some(
    (category) => category.docs.length > 0,
  );
  const avatarAltText = employee
    ? `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim() ||
      "profile"
    : "profile";
  const employeeInitials = getEmployeeInitials(employee);
  const handleCategoryToggle = (categoryKey: string) => {
    setSelectedCategoryKey(categoryKey);
    setCategoryDialogOpen(true);
  };
  const handleCloseCategoryDialog = () => {
    setCategoryDialogOpen(false);
    setSelectedCategoryKey(null);
  };

  const getHistoryPosition = (
    history: EmployeePositionHistoryDto,
    type: "old" | "new",
  ) => {
    if (type === "old") {
      return history.oldPosition || history.position || "-";
    }

    return history.newPosition || history.position || "-";
  };

  const getHistoryChangeDate = (history: EmployeePositionHistoryDto) =>
    history.promotionDate || history.changeDate || history.startDate;

  const renderFormattedDate = (date?: string | null) =>
    date ? formatDate(date) : "-";

  const renderPositionHistory = () => {
    if (isLoadingPositionHistory) {
      return (
        <Stack
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          useFlexGap
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            Loading position history...
          </Typography>
        </Stack>
      );
    }

    if (isPositionHistoryError) {
      return (
        <Alert severity="error">
          Failed to load position history. Please try again later.
        </Alert>
      );
    }

    if (!positionHistory?.length) {
      return (
        <Typography color="text.secondary">
          No position history available.
        </Typography>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Position history">
          <TableHead>
            <TableRow>
              <TableCell>Old Position</TableCell>
              <TableCell>New Position</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Promotion / Change Date</TableCell>
              <TableCell>Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {positionHistory.map((history) => (
              <TableRow key={history.id}>
                <TableCell>{getHistoryPosition(history, "old")}</TableCell>
                <TableCell>{getHistoryPosition(history, "new")}</TableCell>
                <TableCell>{renderFormattedDate(history.startDate)}</TableCell>
                <TableCell>{renderFormattedDate(history.endDate)}</TableCell>
                <TableCell>
                  {renderFormattedDate(getHistoryChangeDate(history))}
                </TableCell>
                <TableCell>{history.duration || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const isDispatcher = Boolean(
    employee?.position?.toLowerCase().includes("dispatcher") ||
    employee?.position?.toLowerCase().includes("dispatch"),
  );

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const openSalaryFiles = async (salary: EmployeeMonthlySalaryDto) => {
    if (!canViewPayslips) return;

    setSelectedSalary(salary);
    setSalaryFiles([]);
    setSalaryFilesError("");
    setSalaryFilesDialogOpen(true);
    setSalaryFilesLoading(true);

    try {
      const { data } = await axios.get<EmployeeFinancialFileDto[]>(
        `${apiVariable}/employee/personal/employeeFinancials/salary/${salary.id}/files`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employeeId: employee.id },
        },
      );
      setSalaryFiles(data);
    } catch {
      setSalaryFilesError("Failed to load payslip files.");
    } finally {
      setSalaryFilesLoading(false);
    }
  };

  const getSalaryFile = async (
    file: EmployeeFinancialFileDto,
    download: boolean,
  ) => {
    if (!canViewPayslips || !selectedSalary) return;

    const setLoading = download
      ? setDownloadingSalaryFileId
      : setViewingSalaryFileId;
    const fileWindow = download ? null : window.open("", "_blank");

    if (!download && !fileWindow) {
      setSalaryFilesError("Allow pop-ups to view the payslip file.");
      return;
    }

    setLoading(file.id);
    setSalaryFilesError("");

    try {
      const action = download ? "/download" : "";
      const { data } = await axios.get(
        `${apiVariable}/employee/personal/employeeFinancials/salary/${selectedSalary.id}/files/${file.id}${action}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { employeeId: employee.id },
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(data);

      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } else {
        fileWindow!.opener = null;
        fileWindow!.location.href = url;
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch {
      fileWindow?.close();
      setSalaryFilesError(
        `Failed to ${download ? "download" : "view"} '${file.fileName}'.`,
      );
    } finally {
      setLoading("");
    }
  };

  const renderPayslips = (showAll = false) => {
    if (!canViewPayslips) return null;

    if (isLoadingSalaries) {
      return (
        <Stack
          flexWrap={{ xs: "wrap", lg: "nowrap" }}
          useFlexGap
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <CircularProgress size={20} />
          <Typography color="text.secondary">Loading payslips...</Typography>
        </Stack>
      );
    }

    if (isSalariesError) {
      return <Alert severity="error">Failed to load payslips.</Alert>;
    }

    if (!salaries.length) {
      return <Typography color="text.secondary">No salary records.</Typography>;
    }

    const sortedSalaries = [...salaries].sort(
      (first, second) => second.year - first.year || second.month - first.month,
    );
    const displayedSalaries = showAll
      ? sortedSalaries
      : sortedSalaries.slice(0, 2);

    return (
      <>
        <Grid container spacing={2}>
          {displayedSalaries.map((salary) => {
            const total = isDispatcher
              ? (salary.dispatchAmount ?? 0) +
                (salary.bonusAmount1 ?? 0) +
                (salary.bonusAmount2 ?? 0)
              : salary.salaryAmount;
            const mdlTotal =
              salary.exchangeRate != null
                ? formatMdlCurrency(
                    salary.mdlTotal ??
                      Math.round(
                        (total * salary.exchangeRate + Number.EPSILON) * 100,
                      ) / 100,
                  )
                : "—";

            const details = isDispatcher
              ? [
                  ["Loads", salary.totalLoads ?? 0],
                  [
                    "McLeod gross",
                    formatCurrency(salary.totalMcLeodGross ?? 0),
                  ],
                  ["Dispatch rate", `${salary.dispatchRate ?? 0}%`],
                  [
                    "Dispatch amount",
                    formatCurrency(salary.dispatchAmount ?? 0),
                  ],
                  ["Bonus 1", formatCurrency(salary.bonusAmount1 ?? 0)],
                  ["Bonus 2", formatCurrency(salary.bonusAmount2 ?? 0)],
                ]
              : [
                  ["Base salary", formatCurrency(salary.salaryAmount)],
                  [
                    "Increase",
                    salary.hasSalaryIncrease
                      ? salary.salaryIncreasePercentage != null
                        ? `${salary.salaryIncreasePercentage}%`
                        : "Yes"
                      : "No",
                  ],
                  [
                    "New salary",
                    salary.newSalaryAmount != null
                      ? formatCurrency(salary.newSalaryAmount)
                      : "—",
                  ],
                ];

            return (
              <Grid item xs={12} lg={6} key={salary.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    borderRadius: 2.5,
                    borderColor: "divider",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: theme.shadows[4],
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                    <Stack
                      flexWrap={{ xs: "wrap", lg: "nowrap" }}
                      useFlexGap
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Stack
                        flexWrap={{ xs: "wrap", lg: "nowrap" }}
                        useFlexGap
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                      >
                        <Box
                          sx={{
                            display: "grid",
                            placeItems: "center",
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            color: "primary.main",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                          }}
                        >
                          <ReceiptLongIcon />
                        </Box>
                        <Box>
                          <Typography fontWeight={700}>
                            {new Date(
                              salary.year,
                              salary.month - 1,
                            ).toLocaleString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Monthly payslip
                          </Typography>
                        </Box>
                      </Stack>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openSalaryFiles(salary)}
                      >
                        View files
                      </Button>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={1.5}>
                      {details.map(([label, value]) => (
                        <Grid item xs={6} key={String(label)}>
                          <Typography variant="caption" color="text.secondary">
                            {label}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <Stack
                        flexWrap={{ xs: "wrap", lg: "nowrap" }}
                        useFlexGap
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total
                          </Typography>
                          <Typography fontWeight={700}>
                            {formatCurrency(total)}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="caption" color="text.secondary">
                            MDL total
                          </Typography>
                          <Typography fontWeight={700} color="primary.main">
                            {mdlTotal}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Exchange rate:{" "}
                        {salary.exchangeRate?.toLocaleString("en-US", {
                          maximumFractionDigits: 8,
                        }) ?? "—"}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {!showAll && salaries.length > 2 && (
          <Box
            sx={{
              flexWrap: { xs: "wrap", lg: "nowrap" },
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button onClick={() => setAllPayslipsDialogOpen(true)}>
              View all
            </Button>
          </Box>
        )}
      </>
    );
  };

  const renderDocumentRow = (doc: EmployeePersonalFileDto) => {
    const row = {
      ...doc,
      firstName: doc.employeeFirstName,
      lastName: doc.employeeLastName,
    };

    return (
      <Stack
        key={doc.id}
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
        }}
      >
        <Box flex={1} sx={{ width: "100%" }}>
          <Typography variant="body2" color="text.secondary">
            {doc.category} · {doc.documentDate}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          useFlexGap
          sx={{
            width: { xs: "100%", sm: "auto" },
            flexWrap: { xs: "nowrap", sm: "wrap" },
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              setViewLoadingId(doc.id);
              try {
                await handleViewFile(row, "personalFolder", token);
              } finally {
                setViewLoadingId(null);
              }
            }}
            disabled={viewLoadingId === doc.id}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {viewLoadingId === doc.id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "View"
            )}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={async () => {
              setDownloadLoadingId(doc.id);
              try {
                await handleDownloadFile(row, "personalFolder", token);
              } finally {
                setDownloadLoadingId(null);
              }
            }}
            disabled={downloadLoadingId === doc.id}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {downloadLoadingId === doc.id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Download"
            )}
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 8 }}>
      <Box
        sx={{
          minHeight: { xs: 320, sm: 250 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          borderRadius: { xs: 0, sm: 4 },
          position: "relative",
          overflow: "hidden",
          mb: 4,
          px: { xs: 3, sm: 5 },
          py: 4,
          boxShadow: `0 18px 45px ${alpha(theme.palette.primary.main, 0.2)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            width: 260,
            height: 260,
            borderRadius: "50%",
            right: -70,
            top: -120,
            bgcolor: "rgba(255,255,255,0.1)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: 180,
            height: 180,
            borderRadius: "50%",
            right: 150,
            bottom: -140,
            bgcolor: "rgba(255,255,255,0.08)",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems="center"
          justifyContent="space-between"
          spacing={3}
          sx={{
            minHeight: { xs: 240, sm: 170 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems="center"
            spacing={3}
            textAlign={{ xs: "center", sm: "left" }}
          >
            {isProfilePhotoLoading ? (
              <Skeleton variant="circular" width={120} height={120} />
            ) : (
              <Avatar
                src={profilePhotoUrl ?? undefined}
                alt={avatarAltText}
                sx={{
                  width: 120,
                  height: 120,
                  border: "5px solid rgba(255,255,255,0.85)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  bgcolor: theme.palette.primary.dark,
                  color: "#fff",
                  fontSize: 36,
                  fontWeight: 700,
                }}
              >
                {employeeInitials || <PersonIcon fontSize="large" />}
              </Avatar>
            )}
            {employee ? (
              <Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 750, color: "#fff", letterSpacing: -0.5 }}
                >
                  {employee.firstName} {employee.lastName}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.82)", mb: 1.5 }}>
                  {employee.position}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent={{ xs: "center", sm: "flex-start" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip
                    icon={<CalendarMonthIcon />}
                    label={`Joined ${new Date(employee.startDate).getFullYear()}`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.16)",
                      color: "#fff",
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                  {employee.deliveryUnit && (
                    <Chip
                      label={employee.deliveryUnit.toUpperCase()}
                      size="small"
                      sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff" }}
                    />
                  )}
                </Stack>
              </Box>
            ) : (
              <Skeleton width={220} height={40} />
            )}
          </Stack>
          {employee && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              width={{ xs: "100%", sm: "auto" }}
            >
              {selectedEmployeeId && role?.includes("HR") && (
                <Box
                  sx={{
                    "& .MuiButton-root": {
                      bgcolor: "#fff",
                      color: "primary.main",
                      px: 3,
                      py: 1,
                      boxShadow: "0 8px 20px rgba(0,0,0,0.14)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                    },
                  }}
                >
                  <UpdateEmployeePopup
                    employee={employee}
                    onUpdated={setEmployee}
                  />
                </Box>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/hr/timeoff/${employee.id}`)}
                disabled={!employee.id}
                sx={{
                  bgcolor: "#fff",
                  color: "primary.main",
                  px: 3,
                  boxShadow: "0 8px 20px rgba(0,0,0,0.14)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                }}
              >
                Manage time off
              </Button>
            </Stack>
          )}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {isMdUp && (
          <Grid item md={3}>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{ position: "sticky", top: 100, p: 1.5, borderRadius: 3 }}
            >
              {NavList}
            </Paper>
          </Grid>
        )}

        <Grid item xs={12} md={9}>
          {!employee ? (
            <Typography>Loading...</Typography>
          ) : (
            <>
              <PaperSection id="overview" title="Overview">
                <Stack spacing={1}>
                  <InfoRow
                    label="Reports To"
                    value={
                      [employee.manager?.firstName, employee.manager?.lastName]
                        .filter(Boolean)
                        .join(" ") || "Not assigned"
                    }
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ pl: 1 }}
                  >
                    <InfoRow
                      label="Position"
                      value={employee.manager?.position}
                    />
                  </Typography>
                  <InfoRow
                    label="Employment Contract"
                    value={
                      contractTypeLabels[employee.contractType] ||
                      employee.contractType
                    }
                  />
                  <InfoRow
                    label="Start Date"
                    value={new Date(employee.startDate).toLocaleDateString()}
                  />
                  <InfoRow
                    label="Time in Current Position"
                    value={getTimeInCurrentPosition(positionHistory)}
                  />
                  <InfoRow
                    label="Accumulated Tenure"
                    value={calculateDuration(employee.startDate, new Date())}
                  />
                  <InfoRow label="Email" value={employee.email} />
                  <InfoRow
                    label="Personal Mobile Phone"
                    value={employee.phoneNumber}
                  />
                </Stack>
              </PaperSection>

              <PaperSection id="basic-info" title="Basic Info">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoRow label="First Name" value={employee.firstName} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow label="Last Name" value={employee.lastName} />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoRow label="Email" value={employee.email} />
                  </Grid>
                </Grid>
              </PaperSection>

              <PaperSection id="personal" title="Personal">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Gender"
                      value={employeeGenderLabel(employee.gender)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Date of Birth"
                      value={new Date(employee.birthDate).toLocaleDateString()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Birthday"
                      value={new Date(employee.birthDate).toLocaleDateString(
                        "en-US",
                        {
                          month: "long",
                          day: "numeric",
                        },
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Age"
                      value={`${calculateAge(employee.birthDate)} years`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Marital Status"
                      value={employee.isMarried ? "Married" : "Single"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Children Count"
                      value={employee.childrenCount ?? 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoRow
                      label="Children Sex"
                      value={
                        employee.childrenCount > 0
                          ? employee.childrenSexes || "-"
                          : "-"
                      }
                    />
                  </Grid>
                </Grid>
              </PaperSection>

              <PaperSection id="contact-details" title="Contact Details">
                <Stack spacing={1}>
                  <InfoRow label="Email" value={employee.email} />
                  <InfoRow label="Phone Number" value={employee.phoneNumber} />
                </Stack>
              </PaperSection>

              <PaperSection id="work" title="Work">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoRow label="Position" value={employee.position} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Legal Position"
                      value={employee.legalPosition || "-"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Start Date"
                      value={new Date(employee.startDate).toLocaleDateString()}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Work Anniversary"
                      value={new Date(employee.startDate).toLocaleDateString(
                        "en-US",
                        {
                          month: "long",
                          day: "numeric",
                        },
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Tenure Duration"
                      value={calculateDuration(employee.startDate, new Date())}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Tenure Years"
                      value={`${calculateYears(
                        employee.startDate,
                        new Date(),
                      )} years`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Second Level Manager"
                      value={`${employee.secondManager?.firstName || ""} ${
                        employee.secondManager?.lastName || "-"
                      }`}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ pl: 1 }}
                    >
                      <InfoRow
                        label="Position"
                        value={employee.secondManager?.position}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoRow
                      label="Are you a manager?"
                      value={employee.isManager ? "Yes" : "No"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InfoRow
                      label="Delivery Unit"
                      value={employee.deliveryUnit.toUpperCase()}
                    />
                  </Grid>
                </Grid>
              </PaperSection>
              {canViewPayslips && (
                <PaperSection id="payslip" title="Payslip">
                  {renderPayslips()}
                </PaperSection>
              )}
              <PaperSection id="position-history" title="Position History">
                {renderPositionHistory()}
              </PaperSection>
              <PaperSection id="documents" title="Documents">
                {isLoadingDocuments ? (
                  <Typography>Loading...</Typography>
                ) : (
                  <Stack spacing={3}>
                    <Box>
                      <Grid container spacing={2}>
                        {documentCategories.map((category) => {
                          const documentCount = category.docs.length;

                          return (
                            <Grid item xs={12} sm={6} md={4} key={category.key}>
                              <Card
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  transition:
                                    "box-shadow 0.2s ease, border-color 0.2s ease",
                                }}
                              >
                                <CardActionArea
                                  onClick={() =>
                                    handleCategoryToggle(category.key)
                                  }
                                  sx={{ flexGrow: 1 }}
                                >
                                  <CardContent
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 1.5,
                                      py: 3,
                                    }}
                                  >
                                    <FolderIcon
                                      color="primary"
                                      sx={{ fontSize: 48 }}
                                    />
                                    <Typography
                                      variant="subtitle1"
                                      sx={{
                                        fontWeight: 600,
                                        textAlign: "center",
                                      }}
                                    >
                                      {category.label}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {documentCount}{" "}
                                      {documentCount === 1
                                        ? "document"
                                        : "documents"}
                                    </Typography>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                    {!hasAnyDocuments && (
                      <Typography color="text.secondary">
                        No documents found.
                      </Typography>
                    )}
                  </Stack>
                )}
              </PaperSection>
              {/* Category Documents Dialog */}
              <Dialog
                open={categoryDialogOpen}
                onClose={handleCloseCategoryDialog}
                fullWidth
                maxWidth="md"
              >
                <DialogTitle>
                  {documentCategories.find((c) => c.key === selectedCategoryKey)
                    ?.label || "Documents"}
                </DialogTitle>
                <DialogContent dividers>
                  <Stack spacing={1.5}>
                    {(() => {
                      const selected = documentCategories.find(
                        (c) => c.key === selectedCategoryKey,
                      );
                      const docs = selected?.docs || [];
                      if (docs.length === 0)
                        return (
                          <Typography color="text.secondary">
                            No documents available.
                          </Typography>
                        );
                      return docs.map((doc) => renderDocumentRow(doc));
                    })()}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseCategoryDialog}>Close</Button>
                </DialogActions>
              </Dialog>
              {canViewPayslips && (
                <Dialog
                  open={allPayslipsDialogOpen}
                  onClose={() => setAllPayslipsDialogOpen(false)}
                  fullWidth
                  maxWidth="lg"
                >
                  <DialogTitle>All Payslips</DialogTitle>
                  <DialogContent dividers>{renderPayslips(true)}</DialogContent>
                  <DialogActions>
                    <Button onClick={() => setAllPayslipsDialogOpen(false)}>
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
              {canViewPayslips && (
                <Dialog
                  open={salaryFilesDialogOpen}
                  onClose={() => setSalaryFilesDialogOpen(false)}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle>
                    Payslip Files
                    {selectedSalary
                      ? ` — ${selectedSalary.month}/${selectedSalary.year}`
                      : ""}
                  </DialogTitle>
                  <DialogContent dividers>
                    {salaryFilesError && (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {salaryFilesError}
                      </Alert>
                    )}
                    {salaryFilesLoading ? (
                      <Box
                        sx={{
                          flexWrap: { xs: "wrap", lg: "nowrap" },
                          display: "flex",
                          justifyContent: "center",
                          py: 4,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table size="small" aria-label="Payslip files">
                          <TableHead>
                            <TableRow>
                              <TableCell>File</TableCell>
                              <TableCell>Size</TableCell>
                              <TableCell>Uploaded</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {salaryFiles.map((file) => (
                              <TableRow key={file.id}>
                                <TableCell>{file.fileName}</TableCell>
                                <TableCell>
                                  {formatFileSize(file.size)}
                                </TableCell>
                                <TableCell>
                                  {dayjs(file.uploadedDate).format(
                                    "MM/DD/YYYY h:mm A",
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    disabled={viewingSalaryFileId === file.id}
                                    onClick={() => getSalaryFile(file, false)}
                                  >
                                    {viewingSalaryFileId === file.id
                                      ? "Opening..."
                                      : "View"}
                                  </Button>
                                  <Button
                                    size="small"
                                    disabled={
                                      downloadingSalaryFileId === file.id
                                    }
                                    onClick={() => getSalaryFile(file, true)}
                                  >
                                    {downloadingSalaryFileId === file.id
                                      ? "Downloading..."
                                      : "Download"}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                            {!salaryFiles.length && (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No payslip files available.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSalaryFilesDialogOpen(false)}>
                      Close
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

interface InfoRowProps {
  label: string;
  value?: string | number | null;
}
const InfoRow = ({ label, value }: InfoRowProps) => (
  <Typography variant="body1" sx={{ overflowWrap: "anywhere" }}>
    <Box component="span" sx={{ fontWeight: 600 }}>
      {label}:
    </Box>{" "}
    {value ?? "-"}
  </Typography>
);

interface PaperSectionProps {
  id: string;
  title: string;
  children: React.ReactNode;
}
const PaperSection = ({ id, title, children }: PaperSectionProps) => (
  <Paper
    id={id}
    elevation={0}
    variant="outlined"
    sx={{
      p: { xs: 2.5, md: 3.5 },
      mb: 3,
      borderRadius: 3,
      borderColor: "divider",
      scrollMarginTop: 100,
      overflow: "hidden",
    }}
  >
    <Stack
      flexWrap={{ xs: "wrap", lg: "nowrap" }}
      useFlexGap
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ mb: 3 }}
    >
      <Box
        sx={{
          width: 5,
          height: 28,
          borderRadius: 3,
          bgcolor: "primary.main",
        }}
      />
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

export default HRHomePage;
